<template>
  <div class="subsidiary-container">
    <div class="header-section">
      <div class="header-content">
        <div class="header-content-top">
          <h1>{{ subsidiaryName }}</h1>
          <p class="subtitle" style="margin-top: -5px; margin-bottom: 10px;">Financial Performance Overview</p>
        </div>
        <div class="month-selector">
          <button class="btn-text" @click="previousMonth">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="current-month">{{ formatMonthYear(selectedMonth) }}</span>
          <button class="btn-text" @click="nextMonth">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <div class="performance-content">
      <div class="card">
        <div class="card-header">
          <div class="header-content">
            <h3>Financial Performance</h3>
            <p class="description">Track revenue and patient metrics over time</p>
          </div>
          <div class="card-actions">
            <template v-if="reportingBasis === 'weekly'">
              <button 
                v-for="period in ['Weekly', 'Monthly']" 
                :key="period"
                class="btn-text"
                :class="{ active: selectedPeriod === period.toLowerCase() }"
                @click="setPeriod(period)"
              >
                {{ period }}
              </button>
            </template>
            <template v-else>
              <button 
                v-for="period in ['Daily', 'Monthly']" 
                :key="period"
                class="btn-text"
                :class="{ active: selectedPeriod === period.toLowerCase() }"
                @click="setPeriod(period)"
              >
                {{ period }}
              </button>
            </template>
          </div>
        </div>
        <div class="chart-container">
          <div v-if="performanceData.length === 0" class="empty-state">
            <div class="empty-icon">📊</div>
            <p class="text-gray-500">No performance data available</p>
            <p class="text-gray-400">Data will appear here once available</p>
          </div>
          <div v-else class="chart-wrapper">
            <PerformanceHighchart 
              :period="selectedPeriod" 
              :data="performanceData"
              :height="400"
            />
          </div>
        </div>
        <div class="metrics-summary" v-if="performanceData.length > 0">
          <div class="metric-card">
            <h4>Total Revenue</h4>
            <p class="metric-value"><CurrencyDisplay :value="totalRevenue" size="large" /></p>
          </div>
          <div class="metric-card">
            <h4>Total Patients</h4>
            <p class="metric-value">{{ totalPatients.toLocaleString() }}</p>
          </div>
          <div class="metric-card">
            <h4>Average Revenue/Patient</h4>
            <p class="metric-value"><CurrencyDisplay :value="averageRevenuePerPatient" size="large" /></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import PerformanceHighchart from '@/components/PerformanceHighchart.vue'

const route = useRoute()
const router = useRouter()
const selectedPeriod = ref('daily')
const performanceData = ref([])
const subsidiaryId = ref(null)
const reportingBasis = ref('daily')
const selectedMonth = ref(new Date())

// Map of subsidiary names to their IDs
const subsidiaryMap = {
  'selgate-healthcare': 1,
  'selcare-clinic': 2,
  'selcare-dental': 3,
  'selcare-diagnostic': 4,
  'selcare-nursing': 5,
  'selcare-pharmacy': 6
}

// State
const subsidiary = ref(null)
const loading = ref(true)
const error = ref(null)

// Computed
const subsidiaryName = computed(() => {
  if (!subsidiary.value) return ''
  return subsidiary.value.name
})

// Get subsidiary ID from route params
const getSubsidiaryId = computed(() => {
  const subsidiaryId = route.params.subsidiary
  return subsidiaryId ? parseInt(subsidiaryId) : null
})

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Add response interceptor for better error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          ElMessage.error('Authentication failed. Please log in again.')
          break
        case 403:
          ElMessage.error('You do not have permission to access this resource.')
          break
        default:
          ElMessage.error('An error occurred while fetching data.')
      }
    } else {
      ElMessage.error('Network error. Please check your connection.')
    }
    return Promise.reject(error)
  }
)

// Methods
const fetchSubsidiaryData = async () => {
  try {
    loading.value = true
    error.value = null
    
    // Get the subsidiary ID from the route
    const subsidiaryId = getSubsidiaryId.value
    if (!subsidiaryId) {
      error.value = 'Invalid subsidiary'
      router.push('/dashboard')
      return
    }
    
    // Fetch subsidiary data
    const response = await api.get(`/api/subsidiary/${subsidiaryId}`)
    subsidiary.value = response.data
    
    // Check if user has access to this subsidiary
    const userData = JSON.parse(sessionStorage.getItem('user'))
    if (userData.role === 'Admin' && userData.subsidiary_id !== subsidiaryId) {
      router.push('/')
      ElMessage.error('You do not have access to this subsidiary')
    }
  } catch (err) {
    error.value = 'Failed to load subsidiary data'
    if (err.response?.status === 404) {
      router.push('/dashboard')
      ElMessage.error('Subsidiary not found')
    }
  } finally {
    loading.value = false
  }
}

const fetchSubsidiaryInfo = async () => {
  try {
    const id = getSubsidiaryId.value
    if (!id) {
      return
    }

    // Reset data before fetching new data
    performanceData.value = []
    
    // Get subsidiary info including reporting basis
    const response = await api.get(`/api/subsidiary/info/${id}`)
    
    if (response.data) {
      reportingBasis.value = response.data.reporting_basis
      // Set initial period based on reporting basis
      selectedPeriod.value = response.data.reporting_basis
      
      // Fetch performance data immediately after getting subsidiary info
      await fetchPerformanceData()
    } else {
      ElMessage.error('Invalid subsidiary information received')
    }
  } catch (error) {
    if (error.response?.status === 403) {
      ElMessage.error('You do not have permission to view this subsidiary')
    } else {
      ElMessage.error('Failed to load subsidiary information')
    }
    // Reset data on error
    performanceData.value = []
  }
}

const fetchPerformanceData = async () => {
  try {
    const id = getSubsidiaryId.value
    if (!id) {
      return
    }

    const month = selectedMonth.value.toISOString().slice(0, 7) // Format: YYYY-MM
    const response = await api.get(`/api/subsidiary/${id}/performance?period=${selectedPeriod.value}&month=${month}`)
    
    if (!response.data) {
      performanceData.value = []
      return
    }
    
    // Format the data for PerformanceHighchart
    if (selectedPeriod.value === 'monthly') {
      // For monthly data, sort by month and format with month names
      const dataArray = Array.isArray(response.data) ? response.data : Object.values(response.data)
      const sortedData = dataArray.sort((a, b) => {
        const [aYear, aMonth] = a.month.split('-')
        const [bYear, bMonth] = b.month.split('-')
        return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1)
      })
      performanceData.value = sortedData.map(item => {
        const [year, month] = item.month.split('-')
        const date = new Date(year, month - 1)
        return {
          date: date.toLocaleString('default', { month: 'short' }),
          revenue: parseFloat(item.revenue) || 0,
          patients: parseInt(item.patients) || 0
        }
      })
    } else {
      // For weekly/daily data, sort by date
      const dataArray = Array.isArray(response.data) ? response.data : Object.values(response.data)
      const sortedData = dataArray.sort((a, b) => new Date(a.date) - new Date(b.date))
      performanceData.value = sortedData.map(item => ({
        date: item.date,
        revenue: parseFloat(item.revenue) || 0,
        patients: parseInt(item.patients) || 0
      }))
    }

  } catch (error) {
    if (error.response?.status === 403) {
      ElMessage.error('You do not have permission to view performance data')
    } else {
      ElMessage.error('Failed to load performance data')
    }
    performanceData.value = []
  }
}

const setPeriod = (period) => {
  const newPeriod = period.toLowerCase()
  
  // Validate the period based on reporting basis
  const validPeriods = reportingBasis.value === 'weekly' ? ['weekly', 'monthly'] : ['daily', 'monthly']
  if (!validPeriods.includes(newPeriod)) {
    return
  }
  
  selectedPeriod.value = newPeriod
  fetchPerformanceData()
}

// Watch for route changes
watch(
  () => route.params.subsidiary,
  async (newSubsidiary) => {
    if (newSubsidiary) {
      await fetchSubsidiaryData()
      await fetchSubsidiaryInfo()
    }
  },
  { immediate: true }
)

// Watch for period changes
watch(selectedPeriod, async (newPeriod) => {
  await fetchPerformanceData()
})

// Add computed properties for metrics
const totalRevenue = computed(() => {
  return performanceData.value.reduce((sum, item) => sum + item.revenue, 0)
})

const totalPatients = computed(() => {
  return performanceData.value.reduce((sum, item) => sum + item.patients, 0)
})

const averageRevenuePerPatient = computed(() => {
  if (totalPatients.value === 0) return 0
  return totalRevenue.value / totalPatients.value
})

// Add month formatting function
const formatMonthYear = (date) => {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' })
}

// Add month navigation functions
const previousMonth = () => {
  const newDate = new Date(selectedMonth.value)
  newDate.setMonth(newDate.getMonth() - 1)
  selectedMonth.value = newDate
  fetchPerformanceData()
}

const nextMonth = () => {
  const newDate = new Date(selectedMonth.value)
  newDate.setMonth(newDate.getMonth() + 1)
  selectedMonth.value = newDate
  fetchPerformanceData()
}
</script>

<style scoped>
.subsidiary-container {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 24px;
}

h3 {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 12px;
}

.performance-content {
  width: 100%;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-text {
  padding: 8px 16px;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}

.btn-text.active {
  background: #2563eb;
  color: white;
}

.btn-text:hover:not(.active) {
  background: #e2e8f0;
  color: #1e293b;
}

.chart-container {
  height: 400px;
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
}

.header-section {
  margin-bottom: 32px;
}

.subtitle {
  color: #64748b;
  font-size: 1.1rem;
  margin-top: 8px;
}

.header-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  line-height: 10px;
  margin: 10px 0px 32px 0px;
}

.description {
  color: #64748b;
  font-size: 0.9rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;
}

.empty-icon {
  font-size: 2.5rem;
  color: #94a3b8;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
}

.metrics-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.metric-card {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
}

.metric-card h4 {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.metric-value {
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 600;
}

.month-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.current-month {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  min-width: 150px;
  text-align: center;
}

.month-selector .btn-text {
  padding: 6px;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.month-selector .btn-text:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.month-selector .btn-text i {
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .subsidiary-container {
    padding: 16px;
  }
  
  .card {
    padding: 16px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .card-actions {
    width: 100%;
    justify-content: space-between;
  }

  .chart-container {
    height: auto;
    min-height: auto;
    padding: 0px;
    margin: 20px 0px 0px 0px;
  }

  .metrics-summary {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .metric-card {
    padding: 12px;
  }
  
  .metric-value {
    font-size: 1.25rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .month-selector {
    width: 100%;
    justify-content: space-between;
  }
}
</style> 